import { logger } from '../utils/logger';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthResponse, LoginDto, RegisterDto } from '../types/auth';
import { API_BASE_URL, makeAuthenticatedRequest } from '../utils/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (loginDto: LoginDto) => Promise<void>;
  register: (registerDto: RegisterDto) => Promise<void>;
  logout: () => void;
  handleAuthError: () => void;
  updateUser: (user: User, newToken?: string) => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Process town decay (called on login/auth restore)
  const processTownDecay = async () => {
    try {
      await makeAuthenticatedRequest('/town/decay/process', { method: 'POST' });
      // Silently process decay - don't show errors to user
    } catch (err) {
      logger.error('Failed to process town decay:', err);
    }
  };

  // Advance the login streak (called on login/auth restore)
  const processDailyCheckin = async () => {
    try {
      await makeAuthenticatedRequest('/daily/checkin', { method: 'POST' });
      // Silently process checkin - the Daily panel surfaces the result
    } catch (err) {
      logger.error('Failed to process daily checkin:', err);
    }
  };

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));

      // Process decay when restoring auth from localStorage
      processTownDecay();
      processDailyCheckin();
    }
    setIsLoading(false);
  }, []);

  const login = async (loginDto: LoginDto) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginDto),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Login failed');
      }

      const authResponse: AuthResponse = await response.json();
      
      const userData: User = {
        username: authResponse.username,
        email: authResponse.email,
        role: authResponse.role,
      };

      setToken(authResponse.token);
      setUser(userData);

      localStorage.setItem('token', authResponse.token);
      localStorage.setItem('user', JSON.stringify(userData));

      // Process decay on successful login
      processTownDecay();
      processDailyCheckin();
    } catch (error) {
      logger.error('Login error:', error);
      throw error;
    }
  };

  const register = async (registerDto: RegisterDto) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registerDto),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Registration failed');
      }

      const authResponse: AuthResponse = await response.json();
      
      const userData: User = {
        username: authResponse.username,
        email: authResponse.email,
        role: authResponse.role,
      };

      setToken(authResponse.token);
      setUser(userData);
      
      localStorage.setItem('token', authResponse.token);
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error) {
      logger.error('Registration error:', error);
      throw error;
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const handleAuthError = () => {
    logout();
    logger.warn('Authentication expired. Please log in again.');
  };

  const updateUser = (userData: User, newToken?: string) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    if (newToken) {
      setToken(newToken);
      localStorage.setItem('token', newToken);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    logout,
    handleAuthError,
    updateUser,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};